// #############################################################################
// #
// #  French translations
// #
// #############################################################################
// This file contains the French translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for french-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to french-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langFr = {
  home: {
    welcome: 'Bienvenue au service {service}',
    enter: 'Entrer'
  },
  account_type: {
    publisher: 'Éditeur',
    consumer: 'Agrégateur',
    author: 'Auteur'
  },
  app: {
    accountNotSubscribedToCurrentService: 'Votre compte n\'est pas abonné au service actuel'
  },
  login: {
    continue: 'Continuer',
    login_tab: 'Connexion',
    logout: 'Déconnexion',
    register_tab: 'Inscription',
    loginTitle: 'Connectez-vous à votre compte',
    chooseSignInMethod: 'Choisissez comment vous connecter',
    noAccount: 'Vous n\'avez pas de compte ?',
    registerButton: 'Créer un compte',
    cancel: 'Annuler',
    identifier: 'Email',
    identifierDescription: 'Vous pouvez vous connecter en utilisant votre email',
    identifierPlaceholder: 'Entrez votre email',
    usernameOrEmail: 'Nom d\'utilisateur ou Email',
    useAnotherAccount: 'Utiliser un autre compte',
    username: 'Nom d\'utilisateur',
    password: 'Mot de passe',
    email: 'Email',
    loginButton: 'Connexion',
    signingIn: 'Connexion en cours...',
    signInWithEmail: 'Se connecter avec email',
    signInWithEmailAgain: 'Demander un nouveau lien de connexion par email',
    signInWithEmailDescription: 'Utilisez votre email de compte',
    signingInWithMagicLink: 'Connexion avec lien magique...',
    signInWithEmailMessageSent: 'Un lien magique a été envoyé à votre email, suivez le lien pour vous connecter.',
    signingInWithEmail: 'Connexion avec email...',
    subscribeButton: 'S\'abonner à {type}',
    registerAsButton: 'S\'inscrire comme {type}',
    showPassword: 'Afficher le mot de passe',
    hidePassword: 'Masquer le mot de passe',
    or: 'ou',
    subscriptionSuccess: 'Abonnement réussi',
    alternativeAccountDescription: 'Votre compte est actuellement non abonné au service actuel. Vous pouvez vous abonner ou changer de service.',
    alternativeAccountGoTo: 'Aller à {type}',
    sameAccount: 'Vous êtes déjà connecté avec le même compte.',
    sessionConflict: {
      title: 'Conflicte de session',
      message: 'Une session est déjà ouverte pour un autre compte.',
      currentSession: 'Session actuelle:',
      magicLinkSession: 'Session de lien magique:',
      question: 'Voulez-vous rester connecté avec la session actuelle ou reconnecter avec la session de lien magique ?',
      stayWithCurrent: 'Reste avec la session actuelle',
      reconnectWithMagicLink: 'Reconnecter avec la session de lien magique'
    },
    error: {
      invalidCredentials: 'Identifiants invalides',
      loginFailed: 'Échec de la connexion',
      apiConnection: 'Erreur de connexion à l\'API',
      subscribeFailed: 'Échec de l\'abonnement: {error}',
      signInWithEmailFailed: 'Échec de la connexion par email: {error} (vérifiez votre email ou nom d\'utilisateur).',
      invalidEmailFormat: 'Veuillez entrer une adresse email valide'
    },
    alternativeAccount: {
      contentCreators: 'Vous êtes un créateur de contenu ?',
      contentCreatorsLink: 'Connectez-vous ici',
      authors: 'Vous êtes un auteur ?',
      authorsLink: 'Connectez-vous ici',
      aggregators: 'Vous êtes un agrégateur ?',
      aggregatorsLink: 'Connectez-vous ici',
      author: 'Auteur',
      consumer: 'Agrégateur',
      publisher: 'Éditeur'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: 'Enregistrement de Compte',
    introduction: {
      title: 'Bienvenue à l\'enregistrement',
      subtitle: 'S\'inscrire au service {service}',
      description: 'Vous êtes sur le point de vous inscrire au service {service}. Cet enregistrement vous permettra d\'accéder à toutes les fonctionnalités et capacités disponibles pour les comptes {service}.',
      startRegistration: 'Commencer l\'enregistrement',
      alreadyHaveAccount: 'Vous avez déjà un compte ? Connexion'
    },
    step_introduction: {
      title: 'Introduction'
    },
    step_account_setup: {
      title: 'Configuration du Profil',
      displayName: 'Nom d\'affichage',
      displayNamePlaceholder: 'Entrez votre nom d\'affichage',
      username: 'Nom d\'utilisateur',
      usernamePlaceholder: 'Entrez votre nom d\'utilisateur',
      confirmPassword: 'Confirmer le Mot de Passe',
      passwordsDoNotMatch: 'Les mots de passe ne correspondent pas',
      password: 'Mot de passe',
      email: 'Email',
      emailPlaceholder: 'Entrez votre adresse email',
      affiliation: 'Affiliation',
      affiliationPlaceholder: 'Université, Entreprise ou Institution',
      homePage: 'Page d\'Accueil / Page Principale',
      homePagePlaceholder: 'https://votre-site-web.com',
      submit: 'Soumettre le Profil',
      submitting: 'Soumission...'
    },
    step_account_verification: {
      title: 'Vérification du Compte',
      tokenLabel: 'Token de Vérification',
      tokenPlaceholder: 'Entrez le token',
      checkEmail: 'Nous avons envoyé un email de vérification à votre adresse. Veuillez vérifier votre boîte de réception et suivre les instructions pour vérifier votre compte.',
      tokenSent: 'Token de vérification envoyé à : {email}',
      resendToken: 'Renvoyer le Token',
      verifyAccount: 'Vérifier le Compte',
      sendingEmail: 'Envoi de l\'Email...',
      resendEmail: 'Renvoyer l\'Email',
      accountVerified: 'Compte vérifié avec succès !',
      redirecting: 'Redirection vers l\'étape suivante...',
      verifying: 'Vérifier l\'Activation',
      sendingToken: 'Envoi du token de vérification...',
      invalidToken: 'Token de vérification invalide. Veuillez réessayer.',
      tooManyAttempts: 'Trop de tentatives échouées. Veuillez demander un nouveau token.',
      noToken: 'Aucun token de vérification trouvé. Veuillez demander un nouveau token.',
      verificationSuccess: 'Vérification email réussie !',
      attemptsRemaining: '{count} tentatives restantes',
      resendSuccess: 'Nouveau token de vérification envoyé à votre email.',
      networkError: 'Erreur réseau. Veuillez vérifier votre connexion et réessayer.',
      checkingActivation: 'Vérification de l\'activation du compte...',
      emailSentTo: 'Email envoyé à :',
      activationEmailResent: 'Email d\'activation envoyé avec succès ! Veuillez vérifier votre boîte de réception.',
      resendFailed: 'Échec de l\'envoi de l\'email d\'activation. Veuillez réessayer.',
      missingIdentifierOrRegistrationToken: 'Identifiant ou jeton d\'enregistrement manquant',
      accountDoesNotHaveAuthorPrivileges: 'Le compte n\'a pas de privilèges d\'auteur. Veuillez contacter le support.',
      accountActivationCheckFailed: 'Échec de la vérification de l\'activation du compte. Veuillez réessayer.',
      accountNotYetActivated: 'Compte pas encore activé. Veuillez vérifier votre email et cliquer sur le lien d\'activation.',
      invalidCredentials: 'Identifiants invalides. Veuillez vérifier votre nom d\'utilisateur et mot de passe.',
      failedToVerifyAccountActivation: 'Échec de la vérification de l\'activation du compte. Veuillez réessayer.',
      failedToResendActivationEmail: 'Échec du renvoi de l\'email d\'activation. Veuillez réessayer.',
      accountRegistrationRejected: 'Enregistrement du compte rejeté. Veuillez contacter le support.'
    },
    step_registration_complete: {
      title: 'Terminer l\'Enregistrement',
      registrationSummary: 'Résumé de l\'Enregistrement',
      profileCreated: 'Profil créé',
      emailsSent: 'Emails envoyés aux éditeurs',
      continueToDashboard: 'Continuer vers le Tableau de Bord',
      continueToLogin: 'Continuer vers la Connexion'
    }
  },
  common: {
    cancel: 'Annuler',
    save: 'Enregistrer'
  },
  navbar: {
    docs: 'Documentation',
    account: 'Compte',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    login: 'Connexion',
    pendingRegistrationDesc: 'Veuillez compléter votre inscription pour accéder à ce service.',
    apps: {
      title: 'Applications',
      publisher: 'Éditeur',
      publisherDesc: 'Passer au service éditeur',
      author: 'Auteur',
      authorDesc: 'Passer au service auteur',
      consumer: 'Agrégateur',
      consumerDesc: 'Passer au service agrégateur',
      website: 'Site web',
      websiteDesc: 'Passer au site web',
      portal: 'Portail',
      portalDesc: 'Passer au portail',
      currentApplicationDesc: 'Service actuel',
      unavailableServiceDesc: 'Service non disponible'
    }
  },
  sidebar: {
    expand: 'Développer',
    collapse: 'Réduire',
    info: 'Info',
    infoDesc: 'Informations sur le service',
    showDescriptions: 'Afficher les descriptions',
    hideDescriptions: 'Masquer les descriptions'
  },
  pagination: {
    previous: 'Précédent',
    next: 'Suivant',
    first: 'Première',
    last: 'Dernière',
    page: 'Page {current} sur {total}',
    itemsPerPage: 'Éléments par page'
  },
  table: {
    loading: 'Chargement...',
    noData: 'Aucune donnée',
    noResults: 'Aucun résultat',
    search: 'Recherche'
  },
  form: {
    submit: 'Envoyer',
    cancel: 'Annuler',
    save: 'Enregistrer',
    loading: 'Chargement...'
  },
  modal: {
    close: 'Fermer',
    confirm: 'Confirmer',
    cancel: 'Annuler',
    save: 'Enregistrer'
  },
  alert: {
    success: 'Succès',
    error: 'Erreur',
    warning: 'Avertissement',
    info: 'Info'
  },
  toast: {
    success: 'Succès',
    error: 'Erreur',
    warning: 'Avertissement',
    info: 'Info',
    close: 'Fermer'
  },
  tooltip: {
    success: 'Succès',
    error: 'Erreur',
    warning: 'Avertissement',
    info: 'Info'
  },
  notification: {
    success: 'Succès',
    error: 'Erreur',
    warning: 'Avertissement',
    info: 'Info'
  },
  notifications: {
    title: 'Notifications',
    empty: 'Aucune notification',
    loading: 'Chargement des notifications...',
    pageInfo: 'Page {current} sur {total}',
    error: 'Erreur de chargement des notifications',
    markAsRead: 'Marquer comme lu',
    markAsUnread: 'Marquer comme non lu',
    open: 'Ouvrir',
    markAllAsRead: 'Marquer toutes comme lu',
    unread: 'Non lu',
    all: 'Toutes',
    noContent: 'Aucun contenu disponible',
    close: 'Fermer',
    reload: 'Recharger les notifications',
    showMore: 'Afficher plus',
    loadingMore: 'Chargement des notifications...',
    errorLoadingMore: 'Erreur de chargement des notifications',
    noMoreNotifications: 'Aucune notification supplémentaire',
    noNotifications: 'Aucune notification',
    noNotificationsFound: 'Aucune notification trouvée'
  },
  account: {
    title: 'Informations du compte',
    profile_section: 'Profil',
    api_keys_section: 'Clés API',
    security_section: 'Sécurité',
    loading: 'Chargement des informations du compte...',
    profile: 'Profil',
    authorProfile: 'Profil d\'auteur',
    publisherProfile: 'Profil d\'éditeur',
    aggregatorProfile: 'Profil d\'agrégateur',
    id: 'ID',
    name: 'Nom',
    email: 'Email',
    services: 'Services',
    memberSince: 'Membre depuis',
    lastLogin: 'Dernière connexion',
    createNewApiKey: 'Créer une nouvelle clé API',
    apiKeysTitle: 'Clés API',
    apiKeys: {
      name: 'Nom',
      keyId: 'ID de clé',
      status: 'Statut',
      lastUsed: 'Dernière utilisation',
      created: 'Créé',
      expires: 'Expire',
      neverUsed: 'Jamais utilisé',
      noKeys: 'Aucune clé API trouvée.',
      advancedControls: 'Pour des contrôles avancés, visitez',
      advancedControlsLink: 'la page de gestion du compte'
    },
    security: {
      changePassword: 'Changer le mot de passe',
      changePasswordDescription: 'Mettez à jour le mot de passe de votre compte. Vous devez entrer votre mot de passe actuel pour confirmer le changement.',
      currentPassword: 'Mot de passe actuel',
      currentPasswordPlaceholder: 'Entrez votre mot de passe actuel',
      newPassword: 'Nouveau mot de passe',
      newPasswordPlaceholder: 'Entrez votre nouveau mot de passe',
      confirmPassword: 'Confirmer le nouveau mot de passe',
      confirmPasswordPlaceholder: 'Ressaisissez votre nouveau mot de passe',
      changePasswordButton: 'Changer le mot de passe',
      changingPassword: 'Changement en cours...',
      passwordChangedSuccess: 'Votre mot de passe a été modifié avec succès.',
      passwordChangeFailed: 'Échec du changement de mot de passe. Vérifiez votre mot de passe actuel et réessayez.',
      resetPassword: 'Réinitialiser le mot de passe',
      resetPasswordDescription: 'Mot de passe oublié ? Nous pouvons envoyer un lien de réinitialisation à votre adresse e-mail enregistrée. Cliquez sur le lien dans l\'e-mail pour définir un nouveau mot de passe.',
      sendResetEmailButton: 'Envoyer l\'e-mail de réinitialisation',
      sendingResetEmail: 'Envoi en cours...',
      resetEmailSent: 'Un e-mail de réinitialisation de mot de passe a été envoyé à votre adresse enregistrée. Consultez votre boîte de réception et suivez le lien pour définir un nouveau mot de passe.',
      resetPasswordFailed: 'Échec de l\'envoi de l\'e-mail de réinitialisation. Réessayez.',
      changePrimaryEmail: 'Changer l\'e-mail principal',
      changePrimaryEmailDescription: 'Mettez à jour votre adresse e-mail principale. Un e-mail de vérification sera envoyé à la nouvelle adresse. Le nouvel e-mail ne sera activé qu\'après vérification.',
      newEmail: 'Nouvel e-mail',
      newEmailPlaceholder: 'Entrez votre nouvelle adresse e-mail',
      confirmEmail: 'Confirmer le nouvel e-mail',
      confirmEmailPlaceholder: 'Ressaisissez votre nouvelle adresse e-mail',
      emailsDoNotMatch: 'Les adresses e-mail ne correspondent pas',
      changeEmailButton: 'Envoyer l\'e-mail de vérification',
      sendingVerification: 'Envoi en cours...',
      emailVerificationSent: 'Un e-mail de vérification a été envoyé à votre nouvelle adresse. Consultez votre boîte de réception et suivez les instructions pour activer le nouvel e-mail.',
      emailChangeFailed: 'Échec de la demande de changement d\'e-mail. Réessayez.'
    }
  },
  profileSetup: {
    displayName: 'Nom d\'affichage',
    displayNamePlaceholder: 'Entrez votre nom d\'affichage',
    displayNameHelp: 'Votre nom d\'affichage sera utilisé pour vous identifier dans le système (ce n\'est pas votre nom d\'utilisateur).',
    title: 'Configuration du Profil',
    description: 'Veuillez fournir vos informations professionnelles pour compléter votre profil.',
    affiliation: 'Affiliation',
    affiliationPlaceholder: 'ex., Université de Cambridge, MIT, Google Research',
    areaOfInterest: 'Domaine d\'Intérêt',
    areaOfInterestPlaceholder: 'ex., Machine Learning, Vision par Ordinateur, NLP',
    homePage: 'Page d\'Accueil',
    homePagePlaceholder: 'https://votre-site-web.com',
    secondaryEmails: 'Emails Secondaires',
    addSecondaryEmail: 'Ajouter un Email Secondaire',
    secondaryEmailPlaceholder: 'votre adresse email',
    secondaryEmailsHelp: 'Vos emails secondaires',
    save: 'Sauvegarder le Profil',
    saving: 'Sauvegarde...',
    nextPage: 'Page Suivante',
    update: 'Mettre à Jour le Profil',
    updating: 'Mise à jour...',
    avatarUrl: 'URL de l\'avatar',
    avatarUrlPlaceholder: 'https://exemple.com/avatar.jpg',
    changeAvatar: 'Changer d\'avatar',
    noAvatar: 'Pas d\'avatar',
    success: {
      saved: 'Profil sauvegardé avec succès !'
    },
    errors: {
      affiliationRequired: 'L\'affiliation est requise.',
      areaOfInterestRequired: 'Le domaine d\'intérêt est requis.',
      invalidHomePage: 'Veuillez entrer une URL valide pour votre page d\'accueil.',
      tooManySecondaryEmails: 'Vous pouvez ajouter un maximum de 5 emails secondaires.',
      invalidSecondaryEmail: 'Veuillez entrer une adresse email valide.',
      saveFailed: 'Échec de la sauvegarde du profil. Veuillez réessayer.',
      invalidAvatarUrl: 'Veuillez saisir une URL d\'avatar valide.'
    }
  },
  profile: {
    key_DISPLAY_NAME: 'Nom d\'affichage',
    key_NAME: 'Nom',
    key_EMAIL: 'Email',
    key_AFFILIATION: 'Affiliation',
    key_AREA_OF_INTEREST: 'Domaine d\'Intérêt',
    key_HOME_PAGE: 'Page d\'Accueil',
    key_EMAILS: 'Emails',
    key_AVATAR_URL: 'URL de l\'avatar',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: 'Configuration du Profil',
    description: 'Veuillez configurer votre profil pour accéder à ce service.',
    update: 'Mettre à Jour le Profil',
    editProfile: 'Modifier le Profil'
  },
  bankInfoForm: {
    title: 'Ajouter/Mettre à jour les informations bancaires',
    accountHolder: 'Nom du titulaire du compte :',
    iban: 'IBAN :',
    bic: 'Code BIC/Swift :',
    save: 'Enregistrer les informations bancaires',
    saved: 'Informations bancaires enregistrées (localement pour cette démonstration).'
  },
  settings: {
    title: 'Paramètres',
    theme: 'Thème',
    light: 'Clair',
    dark: 'Sombre',
    layout: 'Disposition',
    helpEnabled: 'Afficher les messages d\'aide',
    sidebar: 'Barre latérale',
    navbar: 'Barre de navigation',
    save: 'Enregistrer',
    saved: 'Paramètres enregistrés !',
    language: 'Langue',
    experimentations: 'Expérimentations',
    enabled: 'Activé',
    disabled: 'Désactivé'
  },
  apiKeyModal: {
    title: 'Créer une nouvelle clé API',
    namePlaceholder: 'Nom pour la clé API',
    nameHelp: 'Entrez un nom pour la clé API, cela vous aidera à identifier la clé plus tard.',
    createButton: 'Créer la clé API',
    yourKeyIs: 'Votre nouvelle clé API est :',
    copyWarning: 'Copiez et enregistrez cette clé. Vous ne pourrez plus la voir !',
    copy: 'Copier',
    copied: 'Copié !',
    close: 'Fermer',
    error: 'Échec de la création de la clé API.'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: 'Passer en mode clair',
    switchToDark: 'Passer en mode sombre'
  }
};
